import React, { useEffect, useLayoutEffect, useRef, useState, useContext } from "react";
import { auth } from "../../config/firebase";
import { signInWithCustomToken } from "firebase/auth";
import { AuthContext } from "../../context/AuthContext";
import ChatService from "../../services/FirebaseChatService";
import MainButton from "../../components/Elements/MainButton/MainButton";
import MainInput from "../../components/Elements/MainInput/MainInput";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import "./SupportChats.css";

const MOBILE_QUERY = "(max-width: 768px)";

const getMessageDate = (value) => {
  if (!value?.toDate) return null;
  return value.toDate();
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const formatDayLabel = (date) => {
  if (!date) return "";
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(date, today)) return "Today";
  if (isSameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
};

const formatChatListDate = (timestamp) => {
  if (!timestamp?.toDate) return "";
  const date = timestamp.toDate();
  const now = new Date();
  if (isSameDay(date, now)) return "Today";
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const SupportChats = () => {
  const { role, loading: authLoading } = useContext(AuthContext);
  const isAdmin = !authLoading && role?.name === "admin";
  const authReady = !authLoading;

  const [firebaseUser, setFirebaseUser] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [readChatIds, setReadChatIds] = useState(new Set()); // ← lokalne unread
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(MOBILE_QUERY).matches : false,
  );

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const hasInitiallyScrolledRef = useRef(false);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const handleChange = (e) => setIsMobile(e.matches);
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const loginWithBackendToken = async () => {
      const token = localStorage.getItem("firebaseToken");
      if (token && !auth.currentUser) {
        try {
          await signInWithCustomToken(auth, token);
          setFirebaseUser(auth.currentUser);
        } catch (error) {
          console.error("Firebase custom token login failed:", error);
        }
      } else if (auth.currentUser) {
        setFirebaseUser(auth.currentUser);
      }
    };
    loginWithBackendToken();
  }, []);

  useEffect(() => {
    if (!firebaseUser || !isAdmin) return;
    const unsub = ChatService.subscribeToAllChats((nextChats) => {
      setChats(nextChats);
      setSelectedChat((prev) => {
        if (!prev) return nextChats[0] ?? null;
        const updated = nextChats.find((c) => c.id === prev.id);
        return updated ?? nextChats[0] ?? null;
      });
    });
    return unsub;
  }, [firebaseUser, isAdmin]);

  useEffect(() => {
    if (!selectedChat?.id || !firebaseUser || !isAdmin) return;
    const unsub = ChatService.subscribeToChatMessages(selectedChat.id, setMessages);
    return unsub;
  }, [selectedChat?.id, firebaseUser, isAdmin]);

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || messages.length === 0) return;
    if (!hasInitiallyScrolledRef.current) {
      container.scrollTop = container.scrollHeight;
      hasInitiallyScrolledRef.current = true;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  useEffect(() => {
    hasInitiallyScrolledRef.current = false;
  }, [selectedChat?.id]);

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    setReadChatIds((prev) => new Set([...prev, chat.id]));
    ChatService.markAsReadByAdmin(chat.id);
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedChat?.id || !firebaseUser || sending) return;
    try {
      setSending(true);
      setReplyText("");
      await ChatService.sendMessage(selectedChat.id, replyText.trim(), firebaseUser.email, "support");
    } catch (error) {
      console.error("Failed to send support reply:", error);
    } finally {
      setSending(false);
    }
  };

  const handleReplyKeyDown = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      await handleSendReply();
    }
  };

  if (authReady && !isAdmin) {
    return (
      <main>
        <PageHeader title="Support Chats" description="Admin access required." />
        <div className="no-chat-selected">
          <h3>Access Denied</h3>
          <p>You do not have permission to view this page.</p>
        </div>
      </main>
    );
  }

  return (
    <main>
      <PageHeader
        title="Support Chats"
        description="View chat and message back users, to help them with their issues."
      />
      <div className="admin-help-chats">
        <div className="unified-chat-container">
          <div className="chats-split-layout">
            {(!isMobile || !selectedChat) && (
              <div className="chats-list-panel">
                <div className="panel-header">
                  <h2>Chats</h2>
                </div>
                <div className="chats-list">
                  {!authReady && (
                    <div className="loading-state">
                      <h3>Loading chats...</h3>
                    </div>
                  )}
                  {authReady && chats.length === 0 && (
                    <div className="no-chat-selected">
                      <h3>No chats yet</h3>
                    </div>
                  )}
                  {authReady &&
                    chats.map((chat) => {
                      const isUnread =
                        selectedChat?.id !== chat.id &&
                        !readChatIds.has(chat.id) &&
                        chat.lastMessageSender !== "support" &&
                        (!chat.adminLastRead || chat.lastMessageAt?.toDate() > chat.adminLastRead?.toDate());

                      const displayName = chat.email || chat.userId || "Unknown";

                      return (
                        <div
                          key={chat.id}
                          className={`chat-item ${selectedChat?.id === chat.id ? "active" : ""} ${isUnread ? "unread" : ""}`}
                          onClick={() => handleSelectChat(chat)}
                        >
                          <div className="chat-item-top">
                            {/* ← kropka + email w jednym wierszu */}
                            <div className="chat-item-left">
                              {isUnread && <span className="unread-dot" aria-label="Unread" />}
                              <div className="chat-user" title={displayName}>
                                {displayName}
                              </div>
                            </div>
                            <div className="chat-time">{formatChatListDate(chat.lastMessageAt)}</div>
                          </div>
                          <div className="chat-preview">{chat.lastMessage?.slice(0, 50)}...</div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {!isMobile && <div className="vertical-divider" />}

            {(!isMobile || selectedChat) && (
              <div className="chat-detail-panel">
                {selectedChat ? (
                  <>
                    <div className="panel-header">
                      {isMobile && (
                        <button
                          type="button"
                          onClick={() => setSelectedChat(null)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            marginRight: "8px",
                            fontSize: "1.2rem",
                          }}
                          aria-label="Back"
                        >
                          ←
                        </button>
                      )}
                      <h3>Chat with {selectedChat.email || selectedChat.userId || "Unknown user"}</h3>
                    </div>

                    <div className="messages-container" ref={messagesContainerRef}>
                      {messages.length === 0 && !sending ? (
                        <div className="no-chat-selected">
                          <h3>No messages yet</h3>
                        </div>
                      ) : (
                        messages.map((message, index) => {
                          const currentDate = getMessageDate(message.createdAt);
                          const previousDate = index > 0 ? getMessageDate(messages[index - 1].createdAt) : null;
                          const showDateSeparator = index === 0 || !isSameDay(currentDate, previousDate);
                          return (
                            <React.Fragment key={message.id}>
                              {showDateSeparator && currentDate && (
                                <div className="date-divider">{formatDayLabel(currentDate)}</div>
                              )}
                              <div
                                className={`message ${message.sender === "support" ? "admin-message" : "user-message"}`}
                              >
                                <div className="message-bubble">
                                  <div className="message-text">{message.text}</div>
                                  <div className="message-time">
                                    {currentDate
                                      ? currentDate.toLocaleTimeString("pl-PL", {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                          hour12: false,
                                        })
                                      : ""}
                                  </div>
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    <div className="reply-section">
                      <MainInput
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={handleReplyKeyDown}
                        placeholder="Type your reply..."
                        className="reply-input"
                      />
                      <MainButton
                        onClick={handleSendReply}
                        disabled={!replyText.trim() || sending}
                        className="send-button"
                      >
                        {sending ? "Sending..." : "Send Reply"}
                      </MainButton>
                    </div>
                  </>
                ) : (
                  <div className="no-chat-selected">
                    {chats.length === 0 ? <h3>Loading chats...</h3> : <h3>Select a conversation</h3>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default SupportChats;
